import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { environment } from '@environments/environment';
import { reportConsole } from '@utils';
import { GCAuthenticationService } from 'src/app/billing/shared/service/gc-authentication.service';
import GenesysCloudAPIData from '../../shared/static/genesys-cloud-api-data';
import { GenesysCloudAPIOption } from '../../shared/types/genesys-cloud-api-data';

@Component({
    templateUrl: './billing-dialog.component.html',
    styleUrl: './billing-dialog.component.scss',
    standalone: true,
    imports: [
        MatCheckboxModule,
        MatDividerModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatGridListModule,
        MatSelectModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Dialog for selecting a Genesys Cloud region and initiating the OAuth login flow.
 *
 * Filters available regions by environment and redirects to the authorization URL
 * via `GCAuthenticationService`.
 *
 * @example
 * this.dialog.open(BillingDialogComponent);
 */
export class BillingDialogComponent {
    readonly dialogRef = inject<MatDialogRef<BillingDialogComponent>>(MatDialogRef);
    private readonly fb = inject(FormBuilder);
    private readonly gcAuthenticationService = inject(GCAuthenticationService);

    readonly isLoading = signal(false);

    private readonly allOptions: GenesysCloudAPIOption[] = GenesysCloudAPIData;
    readonly filteredSelectOptions = signal<GenesysCloudAPIOption[]>(this.buildFilteredOptions());

    readonly billingForm: FormGroup = this.fb.group({
        region: [
            this.filteredSelectOptions().length > 0 ? this.filteredSelectOptions()[0].auth : '',
            Validators.required,
        ],
    });

    private buildFilteredOptions(): GenesysCloudAPIOption[] {
        const env = environment.name;
        if (['local', 'dev', 'billing-dev'].includes(env)) return this.allOptions;
        if (['uat', 'prod'].includes(env)) return this.allOptions.filter((o) => !o.dev);
        return [];
    }

    /**
     * Handle login to Genesys Cloud
     */
    async loginToGenesysCloud(): Promise<void> {
        if (this.billingForm.invalid) return;

        this.isLoading.set(true);
        const authUrl = this.billingForm.value.region;

        const redirect = await this.gcAuthenticationService.generateAuthorizationUrl(authUrl);
        if (redirect?.success) {
            reportConsole('[BillingDialogComponent] Redirecting to authorization URL', 'info', redirect.url);
            // deepcode ignore OR: is properly checked in the generate authorization url method.
            globalThis.location.href = redirect.url;
        } else {
            this.isLoading.set(false);
        }
    }
}
